import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/system";
import {
  Tooltip,
  IconButton,
  Box,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericRnDListing from "../RnDMember/GenericRnDListing/GenericRnDListing";
import { ESCALATIONS_MANAGER, GET_USER_BY_ROLE, ESCALATED_EXIT_REQUESTS_EXPORT } from "@API/APIRoutes";
import { setData } from "@Redux/requestDataSlice";
import { stringCustomOperators } from "@Utils/Validations/HelperFunctions/CustomFilters";
import { getStatusColor } from "../../Utils/Validations/HelperFunctions/StyleFunction";
import SearchableDropDown from "@Components/DropDown/SearchableDropDown";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { setSnackbar } from "@Redux/snackBarSlice";
import axiosInstance from "@API/axios";
import useGetAll from "../../Hooks/useGetAll";

// ===================== CONSTANTS =====================
const PAGE_TITLE = "Escalated Requests";
const ERROR_MESSAGE = "Failed to fetch escalated requests. Trying again!";

const TOOLTIP_RESPOND = "Respond to Request";
const TOOLTIP_VIEW_DETAILS = "View Request Details";

const COLUMN_HEADERS = {
  INGREDIENT: "Ingredient",
  ORIGINAL_ASSIGNEE: "Original Assignee",
  ESCALATION_TYPE: "Escalation Type",
  ESCALATION_LEVEL: "Escalation Level",
  ESCALATED_AT: "Escalated At",
  DEADLINE: "Primary Date",
  STATUS: "Status",
  MANAGER_ACTION: "Manager Action",
  ACTIONS: "Actions",
  CREATED_BY: "Created By",
};

const DEFAULT_VALUE = "-";

const safeParseDate = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) {
    // Handle Java LocalDateTime array format [year, month, day, hour, minute, second, nano]
    return new Date(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  if (!value) return DEFAULT_VALUE;
  const d = value instanceof Date ? value : safeParseDate(value);
  return d ? d.toLocaleDateString() : DEFAULT_VALUE;
};

const getEscalationStatusColor = (status) => {
  switch (status) {
    case "PENDING":
      return "warning";
    case "IN_PROGRESS":
      return "info";
    case "RESOLVED":
      return "success";
    case "REASSIGNED":
      return "secondary";
    case "RETURNED_TO_ORIGINAL_USER":
      return "default";
    case "ESCALATED_TO_NEXT_LEVEL":
      return "error";
    default:
      return "default";
  }
};

const getManagerActionColor = (action) => {
  switch (action) {
    case "PENDING":
      return "warning";
    case "COMPLETE_BY_MANAGER":
      return "info";
    case "REASSIGN_TO_NEW_USER":
      return "secondary";
    case "RETURN_TO_ORIGINAL_USER":
      return "default";
    default:
      return "default";
  }
};

function EscalatedRequestsListing() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: loadingUsers, getAll } = useGetAll();

  // Reassign dialog state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignEscalation, setReassignEscalation] = useState(null);
  const [reassignUsers, setReassignUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isEmpty, setIsEmpty] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const defaultColumns = [
    {
      field: "ingredientName",
      headerName: COLUMN_HEADERS.INGREDIENT,
      flex: 1.5,
      type: "string",
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredientName}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredientCasNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "requestCreatedBy",
      headerName: COLUMN_HEADERS.CREATED_BY,
      flex: 1.2,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "originalAssigneeEmail",
      headerName: COLUMN_HEADERS.ORIGINAL_ASSIGNEE,
      flex: 1.5,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "escalationType",
      headerName: COLUMN_HEADERS.ESCALATION_TYPE,
      flex: 1.3,
      renderCell: (params) => (
        <Chip
          label={params.row.escalationType?.replace(/_/g, " ")}
          color="info"
          size="small"
          variant="outlined"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "escalationLevel",
      headerName: COLUMN_HEADERS.ESCALATION_LEVEL,
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={`Level ${params.row.escalationLevel || 1}`}
          color={params.row.escalationLevel === 2 ? "error" : "warning"}
          size="small"
          variant="outlined"
        />
      ),
      type: "number",
    },
    {
      field: "escalatedAt",
      headerName: COLUMN_HEADERS.ESCALATED_AT,
      flex: 1.2,
      valueGetter: (params) => safeParseDate(params.row.escalatedAt),
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "requestPrimaryDate",
      headerName: COLUMN_HEADERS.DEADLINE,
      flex: 1.2,
      valueGetter: (params) => safeParseDate(params.row.requestPrimaryDate),
      valueFormatter: (params) => formatDate(params.value),
      type: "date",
    },
    {
      field: "status",
      headerName: COLUMN_HEADERS.STATUS,
      flex: 1.5,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getEscalationStatusColor(params.row.status)}
          size="small"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "managerAction",
      headerName: COLUMN_HEADERS.MANAGER_ACTION,
      flex: 1.5,
      renderCell: (params) => (
        <Chip
          label={params.row.managerAction?.replace(/_/g, " ")}
          color={getManagerActionColor(params.row.managerAction)}
          size="small"
          variant="outlined"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "actions",
      headerName: COLUMN_HEADERS.ACTIONS,
      sortable: false,
      filterable: false,
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      disableReorder: true,
    },
  ];

  const initialColumnVisibilityModel = {};

  const handleRespondClick = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.ApproverEscalatedRequestResponse
        .replace(":reqId", row.exitRequestId)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const handleViewDetailsClick = (row, navigate) => {
    dispatch(setData(row));
    navigate(
      DefaultRoutes.ApproverEscalatedRequestInfo
        .replace(":reqId", row.exitRequestId)
        .replace(":categoryAssignmentId", row.categoryAssignmentId)
    );
  };

  const handleReassignClick = (row) => {
    setReassignEscalation(row);
    setSelectedUserId("");
    setIsEmpty(false);
    setReassignDialogOpen(true);
    // Fetch R&D users for reassignment
    getAll(`${GET_USER_BY_ROLE}?roleName=R%26D`, "Failed to fetch users").then(
      (data) => {
        if (data) {
          setReassignUsers(
            data.map((u) => ({ id: u.id, name: u.username, email: u.email }))
          );
        }
      }
    );
  };

  const handleReassignConfirm = async () => {
    if (!selectedUserId) {
      setIsEmpty(true);
      return;
    }
    setReassigning(true);
    try {
      await axiosInstance.post(
        `/escalations/${reassignEscalation.id}/reassign?newAssigneeId=${selectedUserId}`
      );
      dispatch(
        setSnackbar({
          open: true,
          message: "Request reassigned successfully!",
          error: false,
        })
      );
      setReassignDialogOpen(false);
      setReassignEscalation(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to reassign request.",
          error: true,
        })
      );
    } finally {
      setReassigning(false);
    }
  };

  const handleReassignClose = () => {
    setReassignDialogOpen(false);
    setReassignEscalation(null);
    setSelectedUserId("");
    setIsEmpty(false);
  };

  const renderActions = (params, navigate, theme) => {
    const isPending = params.row.managerAction === "PENDING";
    // Escalation no longer moves the request to the manager; they can respond only on requests
    // they hold themselves. Otherwise it stays with the R&D assignee and is view/reassign only.
    const canRespond = params.row.assignedToManager === true;
    return (
      <ThemeProvider theme={theme}>
        {canRespond && isPending && (
          <Tooltip title={TOOLTIP_RESPOND}>
            <IconButton
              color="success"
              onClick={() => handleRespondClick(params.row, navigate)}
              size="small"
              sx={{ mr: 0 }}
            >
              <PlayArrowRoundedIcon />
            </IconButton>
          </Tooltip>
        )}
        {canRespond && !isPending && (
          <Tooltip title="Edit Impact Assessment">
            <IconButton
              color="primary"
              onClick={() => handleRespondClick(params.row, navigate)}
              size="small"
              sx={{ mr: 0 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={TOOLTIP_VIEW_DETAILS}>
          <IconButton
            color="info"
            onClick={() => handleViewDetailsClick(params.row, navigate)}
            size="small"
            sx={{ mr: 0 }}
          >
            <LightbulbOutlinedIcon />
          </IconButton>
        </Tooltip>
        {isPending && (
          <Tooltip title="Reassign Request">
            <IconButton
              color="warning"
              onClick={() => handleReassignClick(params.row)}
              size="small"
              sx={{ mr: 0 }}
            >
              <SwapHorizIcon />
            </IconButton>
          </Tooltip>
        )}
      </ThemeProvider>
    );
  };

  return (
    <>
      <GenericRnDListing
        key={refreshKey}
        title={PAGE_TITLE}
        PageIcon={<ReceiptLongIcon color="white" fontSize="large" />}
        iconColor="warning.main"
        apiRoute={ESCALATIONS_MANAGER}
        exportApi={ESCALATED_EXIT_REQUESTS_EXPORT}
        exportRowToExitRequestId={(row) => row.exitRequestId}
        defaultColumns={defaultColumns}
        renderActions={renderActions}
        errorMessage={ERROR_MESSAGE}
        buttons={[]}
        getRowId={(row) => row.id}
        initialColumnVisibilityModel={initialColumnVisibilityModel}
        serverPagination={false}
      />

      {/* Reassign Dialog */}
      <Dialog
        open={reassignDialogOpen}
        onClose={handleReassignClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reassign Escalated Request</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Select an R&D member to reassign this request to:
          </Typography>
          <SearchableDropDown
            state={selectedUserId}
            setState={setSelectedUserId}
            name="R&D Member"
            label=""
            items={reassignUsers}
            placeholder="Select member..."
            size="small"
            loading={loadingUsers}
            isEmpty={isEmpty}
            setIsEmpty={setIsEmpty}
            dropDownError={isEmpty}
            disabled={loadingUsers || reassigning}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReassignClose} disabled={reassigning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReassignConfirm}
            disabled={reassigning || loadingUsers || !selectedUserId}
            sx={{ minWidth: 120 }}
          >
            {reassigning ? (
              <LoadingComponent
                size={22}
                thickness={4}
                color="inherit"
                style={{ marginRight: 8 }}
              />
            ) : null}
            {reassigning ? "" : "Reassign"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EscalatedRequestsListing;
